<template>
  <div class="mx-auto w-full max-w-screen-2xl px-6 pb-8 md:px-8 lg:px-16">
    <div class="flex min-w-full flex-col-reverse items-center justify-center gap-4 pt-4 pb-12 min-h-fill-base-layout lg:flex-row lg:px-12">
      <div class="w-full lg:w-5/12">
        <h1 v-if="title" class="mb-3 text-center text-3xl font-bold lg:text-left">
          {{ title }}
        </h1>
        <div v-if="subtitle"
          class="mt-1 flex items-center justify-center text-center text-slate-600 gap-1.5 lg:justify-start lg:text-start">
          <svg
            v-if="showLock"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6.99992 7.58329C6.82232 7.58122 6.64835 7.6336 6.50143 7.73339C6.3545 7.83317 6.24167 7.97558 6.17813 8.14143C6.11458 8.30728 6.10337 8.48862 6.14601 8.66104C6.18864 8.83345 6.28307 8.98867 6.41659 9.10579V9.91663C6.41659 10.0713 6.47804 10.2197 6.58744 10.3291C6.69684 10.4385 6.84521 10.5 6.99992 10.5C7.15463 10.5 7.303 10.4385 7.4124 10.3291C7.52179 10.2197 7.58325 10.0713 7.58325 9.91663V9.10579C7.71677 8.98867 7.8112 8.83345 7.85383 8.66104C7.89647 8.48862 7.88526 8.30728 7.82171 8.14143C7.75817 7.97558 7.64534 7.83317 7.49841 7.73339C7.35149 7.6336 7.17752 7.58122 6.99992 7.58329ZM9.91659 5.24996V4.08329C9.91659 3.30974 9.60929 2.56788 9.06231 2.0209C8.51533 1.47392 7.77347 1.16663 6.99992 1.16663C6.22637 1.16663 5.48451 1.47392 4.93752 2.0209C4.39054 2.56788 4.08325 3.30974 4.08325 4.08329V5.24996C3.61912 5.24996 3.174 5.43433 2.84582 5.76252C2.51763 6.09071 2.33325 6.53583 2.33325 6.99996V11.0833C2.33325 11.5474 2.51763 11.9925 2.84582 12.3207C3.174 12.6489 3.61912 12.8333 4.08325 12.8333H9.91659C10.3807 12.8333 10.8258 12.6489 11.154 12.3207C11.4822 11.9925 11.6666 11.5474 11.6666 11.0833V6.99996C11.6666 6.53583 11.4822 6.09071 11.154 5.76252C10.8258 5.43433 10.3807 5.24996 9.91659 5.24996ZM5.24992 4.08329C5.24992 3.61916 5.43429 3.17404 5.76248 2.84586C6.09067 2.51767 6.53579 2.33329 6.99992 2.33329C7.46405 2.33329 7.90917 2.51767 8.23736 2.84586C8.56554 3.17404 8.74992 3.61916 8.74992 4.08329V5.24996H5.24992V4.08329ZM10.4999 11.0833C10.4999 11.238 10.4385 11.3864 10.3291 11.4958C10.2197 11.6052 10.0713 11.6666 9.91659 11.6666H4.08325C3.92854 11.6666 3.78017 11.6052 3.67077 11.4958C3.56138 11.3864 3.49992 11.238 3.49992 11.0833V6.99996C3.49992 6.84525 3.56138 6.69688 3.67077 6.58748C3.78017 6.47808 3.92854 6.41663 4.08325 6.41663H9.91659C10.0713 6.41663 10.2197 6.47808 10.3291 6.58748C10.4385 6.69688 10.4999 6.84525 10.4999 6.99996V11.0833Z"
              fill="#059669"
            />
          </svg>
          {{ subtitle }}
        </div>
        <slot name="default" />
        <div class="mt-9 flex flex-col sm:flex-row-reverse items-center justify-center gap-2 lg:justify-end">
          <FormButton
            v-if="canGoToPrevStep"
            color="secondary"
            type="button"
            size="lg-wide"
            class="font-medium"
            @click="handlePrevStep"
          >
            Back
          </FormButton>
          <FormButton
            v-if="canSkip"
            color="secondary"
            size="lg-wide"
            class="font-medium"
            @click="handleNextStep(true)"
            >Skip
          </FormButton>
          <FormButton
            color="gradient"
            type="button"
            size="lg-wide"
            class="font-medium"
            :loading="loading"
            @click="handleNextStep(false)"
            :disabled="!isReadyForNextStep"
          >
            <slot name="next-step-text">Next Step</slot>
          </FormButton>
          <FormButton
            v-if="snooze"
            color="secondary"
            size="lg-wide"
            @click="snooze">
            Remind Me Later
          </FormButton>
        </div>
        <div class="mt-8">
          <slot name="footer-text" />
        </div>
      </div>
      <div class="w-9/12 sm:w-8/12 md:w-7/12 lg:w-6/12 lg:scale-110">
        <slot name="splash-image" />
      </div>
    </div>
  </div>
</template>

<script setup>
import FormButton from "./FormButton.vue";

defineProps({
  isReadyForNextStep: {
    type: Boolean,
    required: true,
  },
  snooze: {
    type: Function,
    default: null,
  },
  title: String,
  subtitle: String,
  canSkip: Boolean,
  showLock: Boolean,
  canGoToPrevStep: Boolean,
  loading: Boolean,
});

const emits = defineEmits(["handle-next-step", "handle-prev-step"]);

const handleNextStep = (wasSkipped) => {
  emits("handle-next-step", wasSkipped);
};

const handlePrevStep = () => {
  emits("handle-prev-step");
};
</script>
